import React, { useState, useEffect } from "react";
import {
  Grid,
  Button,
  TextField,
  Typography,
  Box,
  Switch,
} from "@mui/material";
import { LookupUserDTOV1, SiteDTO } from "@beable/roster-backend-client-v1";
import userSupportService from "../../services/UserSupportService";
import UserSearchResults from "./UserSearchResults";

interface UserSearchProps {
  site: SiteDTO | undefined;
  orgId: string | undefined;
}

export default function UserSearch({ site, orgId }: UserSearchProps) {
  const initialUserValue: LookupUserDTOV1 = new LookupUserDTOV1();
  initialUserValue.id = ""; //so that we know we did not find a user after all
  const initialUsersValue: LookupUserDTOV1[] = [];

  const [inputs, setInputs] = useState({ lookupUser: "" });
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState(initialUsersValue); // all users coming back from the lookup
  const [expandedSearch, setExpandedSearch] = useState<string>('off');

  useEffect(() => {
    if (!site) {
      setHasSearched(false);
    }
  }, [site]);

  const handleInputChange = (event: any) => {
    event.persist();
    setInputs((inputs) => ({
      ...inputs,
      [event.target.name]: event.target.value,
    }));
    setHasSearched(false);
  };
  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      handleUserSearch();
    }
  };

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const toggle = event.target.checked ? 'on' : 'off';
    setExpandedSearch(toggle);

    if (site && inputs.lookupUser && hasSearched) {
      handleUserSearch(toggle);
    }
  }

  const handleUserSearch = async (expanded?: string) => {
    setIsLoading(true);

    const result = await userSupportService.lookupUser(
      inputs.lookupUser,
      site!.id,
      isSearchExpanded(expanded ?? expandedSearch)
    );

    setUsers(result);

    setHasSearched(true);
    setIsLoading(false);
  };

  const isSearchExpanded = (expandedState: string) => {
    return (expandedState === 'on');
  };

  function userSearchResults() {
    if (!site || !users.length || !hasSearched) {
      return;
    }

    return (
      <Box sx={{
        marginY: 2
      }}>
        <UserSearchResults users={users} site={site} orgId={orgId} />
      </Box>
    );
  }

  return (
    <Grid size={12}>
      {/* USER LOOKUP */}
      <TextField
        variant="outlined"
        margin="normal"
        required
        fullWidth
        id="lookup"
        label="Lookup User"
        name="lookupUser"
        value={inputs.lookupUser}
        onChange={handleInputChange}
        autoComplete="Lookup User"
        disabled={isLoading || !site}
        onKeyPress={handleKeyPress}
        slotProps={{
          htmlInput: { "data-testid": "input-lookup-username" }
        }}
      />
      <Grid container>
        <Grid sx={{ display: "flex", justifyContent: "flex-start" }} size={6}>
          <Button
            variant="contained"
            aria-label="search-button"
            color="primary"
            disabled={isLoading || !site || !inputs.lookupUser}
            onClick={() => handleUserSearch()}
          >
            Search
          </Button>
        </Grid>
        <Grid sx={{ display: "flex", justifyContent: "flex-end" }} size={6}>
          <Typography component="div">
            <Grid component="label" container spacing={1} sx={{
              alignItems: "center"
            }}>
              <Grid sx={{ fontWeight: 'bold' }}>Include Login Tickets: </Grid>
              <Grid>Off</Grid>
              <Grid>
                <Switch
                  checked={isSearchExpanded(expandedSearch)}
                  onChange={handleToggleChange}
                  name="expandedSearch"
                />
              </Grid>
              <Grid>On</Grid>
            </Grid>
          </Typography>
        </Grid>
      </Grid>
      {hasSearched && !users.length ? (
        <Typography>No user found.</Typography>
      ) : undefined}
      {userSearchResults()}
    </Grid>
  );
}
